import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import 'moment/locale/es-mx';
import esES from 'antd/es/locale/es_ES';
import {
  Form,
  Row,
  Col,
  Spin,
  Typography,
  Card,
  Button,
  Tabs,
  message,
  DatePicker,
  Tooltip,
  ConfigProvider,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  CalculatorOutlined,
  ArrowLeftOutlined,
  PrinterFilled,
  InfoCircleOutlined,
  DollarCircleOutlined,
} from '@ant-design/icons';
import { onError } from 'utils/handlers';
import CustomTable from 'components/Table';
import NumericInput from 'components/NumericInput';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import Select from 'components/Select';
import Previewer from 'components/Previewer';
import { getUsuario, getUsuarios } from 'api/usuarios/usuarios';
import { getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import { getCortes, patchCorte, reabrirCorte } from 'api/recaudacion/cortes';
import { retiroDeCaja } from 'api/configuracion';
import { getReporte } from 'api/recaudacion/index';
import { DATE_FORMAT, DATE_FRONTEND_FORMAT, dateFormatterToReporteador } from 'utils/formatValues';
import { InputSearchPro } from 'components/InputSearch';
import BusquedaAvanzadaUsers from 'components/BusquedaAvanzadaCajeros';
import { getTerminalesDeNetPay } from 'api/configuracion/terminalDeNetPay';
import { postObtenerReporte } from 'api/reporteador';
import usePermissionMessage from 'hooks/usePermissionMessage';
import ResumenRegistroDeRecaudacion from './ResumenRegistroDeRecaudacion';
import ModalReabrir from './ModalReabrir';
import { permissionConfig as permissionConfigAbrir } from './AbrirCorte';

const { Text, Title } = Typography;
const { TabPane } = Tabs;
const ENV_NAME = process.env.REACT_APP_ENV_NAME;

export const permissionConfig = {
  mainPermission: 'viewcrud_corte',
  required: ['view_unidadderecaudacion', 'view_usuario'],
  customActions: [
    {
      name: 'retirocaja',
      title: 'Retiro de caja',
      permissions: ['add_retirodecaja'],
    },
    {
      name: 'editar_corte',
      title: 'Editar corte',
      permissions: ['change_corte', ...permissionConfigAbrir.required],
    },
    {
      name: 'reabrir_corte',
      title: 'Reabrir corte',
      permissions: [permissionConfigAbrir.mainPermission, ...permissionConfigAbrir.required],
    },
  ],
};

// eslint-disable-next-line react/prop-types
const BuscarCorte = ({ permission }) => {
  const history = useHistory();
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const entidad = useSelector(({ auth }) => auth.entidad);
  const user = useSelector(({ auth }) => auth.user);
  const [form] = Form.useForm();
  const [formReabrir] = Form.useForm();
  const [formRetiros] = Form.useForm();
  const [formUpdate] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [cajero, setCajero] = useState([]);
  const [unidadesDeRecaudacion, setUnidadesDeRecaudadacion] = useState([]);
  const [cortes, setCortes] = useState([]);
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [terminalesNetPay, setTerminalesNetpay] = useState([]);
  const [visible, setVisible] = useState(false);
  const [base64, setBase64] = useState();
  const [usuarios, setUsuarios] = useState([]);
  const [recordData, setRecordData] = useState([]);
  const [visibleAdvancedSupervisor, setVisibleAdvancedSupervisor] = useState(false);
  const [visibleModalReabrir, setVisibleModalReabrir] = useState(false);
  const [errorPreviewer, setErrorPreviewer] = useState('');
  const [fileName, setFileName] = useState('Documento');
  const PROD = process.env.REACT_APP_ENV === 'PRODUCTION';

  usePermissionMessage(permission);
  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      const dataTerminales = await getTerminalesDeNetPay();
      const dataUnidadesDeRecaudacion = await getUnidadesDeRecaudacion();
      if (mounted) {
        setUnidadesDeRecaudadacion(dataUnidadesDeRecaudacion);
        setTerminalesNetpay(dataTerminales);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    formRetiros.resetFields();
    formUpdate.resetFields();
    setSelectedRowKeys([]);
    setRecordData([]);
    setFileName('Documento');
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const params = form.getFieldsValue();
      if (params?.apertura) {
        params.apertura_after = params.apertura[0].format(DATE_FORMAT);
        params.apertura_before = params.apertura[1].format(DATE_FORMAT);
        delete params.apertura;
      }
      if (params?.cierre) {
        params.cierre_after = params.cierre[0].format(DATE_FORMAT);
        params.cierre_before = params.cierre[1].format(DATE_FORMAT);
        delete params.cierre;
      }
      delete params.q;
      const dataCortes = await getCortes(params);
      const cajerosIds = dataCortes.map((e) => e.usuario_cajero);
      const _cajeros = [...new Set(cajerosIds)];
      const promises = _cajeros.map((e) => getUsuario(e));
      const usuariosCajeros = await Promise.all(promises);
      setUsuarios(usuariosCajeros.map((e) => ({
        ...e,
        nombre_completo: `${e?.first_name || ''} ${e?.last_name || ''} ${e?.second_last_name || ''}`,
      })));
      setData(dataCortes);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onFinish = async () => {
    setLoading(true);
    const values = formUpdate.getFieldsValue();
    const [id] = selectedRowKeys;
    const updated = await patchCorte(id, values);
    if (updated) {
      onCancel();
    }
    setLoading(false);
  };

  const onFinishReabrir = async (values) => {
    setLoading(true);
    const [id] = selectedRowKeys;
    if (recordData.id === id) {
      const successed = await reabrirCorte(id, values);
      if (successed) {
        handleSearch();
      }
    }
    setLoading(false);
  };

  const onFinishRetiro = async () => {
    setLoading(true);
    await formRetiros.validateFields();
    const [id] = selectedRowKeys;
    const values = formRetiros.getFieldsValue();
    values.corte = cortes.id;
    if (cortes.id === id) {
      const successed = await retiroDeCaja(values);
      if (successed) onCancel();
    } else {
      message.warning('El corte esta cerrado, intenta con uno abierto');
      onCancel();
    }
    setLoading(false);
  };
  const msgRedirectCajaRecibos = () => (
    message.info((
      <>
        <span>
          {recordData.cierre ? 'El corte no cuenta con recibos. Necesita acceder a la Caja y realizar un pago para generar un recibo.'
            : 'No se puede realizar la acción. Se necesita cerrar el corte'}
          {recordData.cierre ? (
            <Button
              type="link"
              onClick={() => {
                history.push('/recaudacion/caja');
                message.destroy();
              }}
            >
              Ir a Caja
            </Button>
          ) : (
            `${''}`
          )}

        </span>
      </>

    ), 3)
  );

  const setBaseAndShowPreview = (_base64) => {
    setBase64(_base64);
    setVisiblePreview(true);
  };

  const reportCorteMethods = async (uri) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const result = await getReporte(key, uri);
    if (result?.success) {
      setBase64(result.base64);
      setVisiblePreview(true);
    } else {
      msgRedirectCajaRecibos();
    }
    setLoading(false);
  };

  const base64ToBlob = (_base64, type = 'application/octet-stream') => {
    const binStr = atob(_base64);
    const len = binStr.length;
    const arr = new Uint8Array(len);
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < len; i++) {
      arr[i] = binStr.charCodeAt(i);
    }
    return new Blob([arr], { type });
  };

  const getReporteMovimientos = async () => {
    setLoading(true);
    // ID migob.digital = 4
    // ID prod = 49
    // ID pruebas = 77
    const values = {
      ID: 195,
      Parametros: [{
        Nombre: 'corte_id',
        TipoParametro: 'Entero',
        Valor: recordData.id,
      }],
    };
    const response = await postObtenerReporte(values);
    const cleanBase64 = response && (response?.replace('data:application/pdf;base64,', '') || '');
    const base64Len = cleanBase64?.length;
    if (base64Len > 0 && base64Len < 2000000) {
      setBaseAndShowPreview(response);
    } else {
      setErrorPreviewer('El archivo es demasiado grande para ser visualizado. Se abrirá en una nueva ventana con el PDF. En caso de que no se abra, favor de habilitar ventanas emergentes en su navegador');
      const blob = base64ToBlob(cleanBase64, 'application/pdf');
      const url = URL.createObjectURL(blob);
      const pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write(`<iframe width='100%' height='100%' src='${url}'></iframe>`);
      }
      setFileName('Detalle de Movimientos');
      setVisiblePreview(true);
    }
    setLoading(false);
  };

  const reportsURSMethods = async (uri) => {
    setLoading(true);
    if (recordData.apertura) {
      const result = await getReporte(recordData.unidad_de_recaudacion, uri, recordData.apertura);
      if (result?.success) {
        setBase64(result.base64);
        setVisiblePreview(true);
      }
    } else {
      message.warn('Operación no permitida, este corte aún se encuentra abierto.');
    }
    setLoading(false);
  };

  const getReporteDeMovimientoGlobalPorRecaidadora = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((item) => item.id === key);
    if (!match.cierre) {
      message.warn('Operación no permitida, este corte aún se encuentra abierto.');
      setLoading(false);
      return;
    }

    const fechaInicio = dateFormatterToReporteador(match.apertura);
    const fechaFinal = dateFormatterToReporteador(match.cierre, false);
    const ID = PROD ? 165 : 181;

    const values = {
      ID,
      Parametros: [
        {
          Nombre: 'FechaInicio',
          TipoParametro: 'Texto',
          Valor: fechaInicio,
        },
        {
          Nombre: 'FechaFinal',
          TipoParametro: 'Texto',
          Valor: fechaFinal,
        },
        {
          Nombre: 'UnidadDeRecaudacion',
          TipoParametro: 'Texto',
          Valor: match.unidad_de_recaudacion,
        },
      ],
    };

    const _base64 = await postObtenerReporte(values);

    setLoading(false);
    setBaseAndShowPreview(_base64);
  };

  const getPrintOptions = () => [
    {
      onClick: () => reportsURSMethods('/entrega-de-caja-global/'),
      icon: <PrinterFilled />,
      text: 'Entrega de Caja Global',
      disabled: !selectedRowKeys.length,
    },
    {
      // onClick: () => reportsURSMethods('/resumen-de-movimiento-global-por-recaudadora/'),
      onClick: () => getReporteDeMovimientoGlobalPorRecaidadora(),
      icon: <PrinterFilled />,
      text: 'Resumen de Movimiento Global por Recaudadora',
      disabled: !selectedRowKeys.length,
    },
    {
      onClick: () => setVisibleModalReabrir(true),
      icon: <DollarCircleOutlined />,
      text: 'Reabrir',
      disabled: !selectedRowKeys.length || !permission.reabrir_corte,
    },
  ];

  const rules = {
    requiredOr: [
      {
        validator: async () => {
          const values = form.getFieldsValue();
          const keys = Object.keys(values);
          const validProps = keys.filter((prop) => values[prop]);
          if (!validProps.length) {
            throw new Error('Debe ingresar al menos un campo');
          }
        },
      },
    ],
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Apertura',
      dataIndex: 'apertura',
      key: 'apertura',
      width: 300,
      render: (val) => (val?.format?.(DATE_FRONTEND_FORMAT) || val),
    },
    {
      titleText: 'Cierre',
      dataIndex: 'cierre',
      key: 'cierre',
      width: 300,
      render: (val) => (val?.format?.(DATE_FRONTEND_FORMAT) || val),
    },
    {
      titleText: 'Unidad Recaudadora',
      dataIndex: 'unidad_de_recaudacion',
      key: 'unidad_de_recaudacion',
      width: 300,
      catalog: {
        cat: unidadesDeRecaudacion,
        labelProp: 'descripcion',
        use: false,
      },
    },
    {
      titleText: 'Cajero',
      dataIndex: 'usuario_cajero',
      key: 'usuario_cajero',
      width: 300,
      render: (val) => usuarios.find((e) => e.id === val)?.nombre_completo,
    },
  ];

  const getConcentradoDeIngresos = async (altern = false) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((item) => item.id === key);
    if (user.email !== 'pagoenlinea@nav.com' && !match.cierre) {
      message.warn('Operación no permitida, este corte aún se encuentra abierto.');
      setLoading(false);
      return;
    }
    const ID = parseInt(process.env.REACT_APP_ID_REPORTE_CONCENTRADO_DE_INGRESOS_POR_CAJERO, 10);
    // eslint-disable-next-line max-len
    const ALTERNID = parseInt(process.env.REACT_APP_ID_REPORTE_CONCENTRADO_DE_INGRESOS_POR_CAJERO_CON_DEPOSITOS, 10);

    const values = {
      // ID: isProd ? 182 : 183,
      ID: altern ? ALTERNID : ID,
      Parametros: [
        {
          Nombre: 'corte_id',
          TipoParametro: 'Entero',
          Valor: match.id,
        },
      ],
    };

    const _base64 = await postObtenerReporte(values);
    setLoading(false);
    setBaseAndShowPreview(_base64);
  };

  const getTramitesPorCajero = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((item) => item.id === key);
    if (!match.cierre) {
      message.warn('Operación no permitida, este corte aún se encuentra abierto.');
      setLoading(false);
      return;
    }
    const fechaInicio = dateFormatterToReporteador(match.apertura);
    const fechaFinal = dateFormatterToReporteador(match.cierre, false);
    const ID = 239;

    const values = {
      ID,
      Parametros: [
        {
          Nombre: 'FECHAINICIO',
          TipoParametro: 'Fecha',
          Valor: fechaInicio,
        },
        {
          Nombre: 'FECHAFINAL',
          TipoParametro: 'Fecha',
          Valor: fechaFinal,
        },
        {
          Nombre: 'CAJERO',
          TipoParametro: 'Entero',
          Valor: match.usuario_cajero,
        },
      ],
    };

    const _base64 = await postObtenerReporte(values);
    setLoading(false);
    setBaseAndShowPreview(_base64);
  };

  const getCorteDeCaja = async () => {
    setLoading(true);

    const values = {
      ID: 32,
      Parametros: [
        {
          Nombre: 'corte_id',
          TipoParametro: 'Entero',
          Valor: selectedRowKeys[0],
        },
      ],
    };

    const _base64 = await postObtenerReporte(values);
    setLoading(false);
    setBaseAndShowPreview(_base64);
  };

  const controls = [
    {
      onClick: () => setVisible(false),
      icon: <ArrowLeftOutlined />,
      text: 'Regresar',
    },
    {
      onClick: getCorteDeCaja,
      icon: <PrinterFilled />,
      text: 'Corte de caja',
    },
    {
      onClick: () => reportCorteMethods('/entrega-de-caja-por-cajero/'),
      icon: <PrinterFilled />,
      text: 'Entrega de Caja',
    },
    {
      onClick: () => getConcentradoDeIngresos(),
      icon: <PrinterFilled />,
      text: 'Concentrado de Ingresos por Cajero',
      disabled: !selectedRowKeys.length,
    },
    ENV_NAME === 'nayarit' && {
      onClick: () => getTramitesPorCajero(),
      icon: <PrinterFilled />,
      text: 'Trámites por Cajero Archivo',
      disabled: !selectedRowKeys.length,
    },
    {
      onClick: () => getReporteMovimientos(),
      icon: <PrinterFilled />,
      text: 'Detalle de Movimientos',
      disabled: !selectedRowKeys.length,
    },
  ];

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setRecordData({
      ...record,
      apertura: record?.apertura?.format('DD-MM-YYYY'),
      cierre: record?.cierre?.format('DD-MM-YYYY'),
    });
    formReabrir.setFieldsValue(record);
    formUpdate.setFieldsValue({
      terminal_de_netpay: record.terminal_de_netpay?.id,
    });
  };

  const onClickEdit = () => {
    setVisible(true);
    const [key] = selectedRowKeys;
    const match = data.find((item) => item.id === key);
    setCortes(match);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const wipe = () => {
    form.resetFields();
    setData([]);
  };

  const searchControls = [
    {
      onClick: () => wipe(),
      icon: <DeleteOutlined />,
      text: 'Limpiar Busqueda',
    },
    {
      onClick: () => handleSearch(),
      icon: <SearchOutlined />,
      text: 'Buscar',
    },
  ];

  const reset = () => {
    setCajero([]);
    form.resetFields(['q']);
  };

  const searchCajero = async () => {
    try {
      setLoading(true);
      const { q: email } = form.getFieldsValue();
      const resCajero = await getUsuarios({ email });

      if (resCajero.length) {
        form.setFieldsValue({
          usuario_cajero: resCajero[0].id,
        });
        const _cajero = resCajero.map((e) => ({
          ...e,
          nombre_completo: `${e?.first_name} ${e?.last_name || ''} ${e?.second_last_name || ''}`,
        }));
        setCajero([_cajero[0]]);
      } else {
        message.info('No se encontraron coincidencias');
        setCajero([]);
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible && selectedRowKeys ? (
          <Card
            bordered={false}
            className="form-container"
            title="Buscar Corte"
            extra={(
              <FormSubmitCustomControls
                controls={searchControls}
              />
            )}
          >
            <Form
              layout="vertical"
              form={form}
              scrollToFirstError
              onFinish={() => handleSearch()}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <ConfigProvider locale={esES}>
                    <Form.Item
                      name="apertura"
                      label="Fecha de Apertura"
                      hasFeedback
                      rules={rules.fecha_vencimiento}
                    >
                      <DatePicker.RangePicker
                        placeholder=""
                        format={DATE_FRONTEND_FORMAT}
                        locale={esES}
                      />
                    </Form.Item>
                  </ConfigProvider>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="cierre"
                    label="Fecha de Cierre"
                    hasFeedback
                    rules={rules.fecha_vencimiento}
                  >
                    <DatePicker.RangePicker
                      placeholder=""
                      format={DATE_FRONTEND_FORMAT}

                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="unidad_de_recaudacion"
                    label="Unidad de recaudación"
                    hasFeedback
                    rules={rules.requiredOr}
                  >
                    <Select
                      disabled={!permission.view_unidadderecaudacion}
                      dataSource={unidadesDeRecaudacion}
                    />
                  </Form.Item>
                </Col>
                <InputSearchPro
                  label="Cajero"
                  tooltip="Búsqueda por Correo del Cajero"
                  name="q"
                  rules={rules.email}
                  onClickAdvanced={() => setVisibleAdvancedSupervisor(true)}
                  onPressEnter={() => searchCajero()}
                  breakPoints={{ md: 8 }}
                  resultProps={{
                    name: 'usuario_cajero',
                    label: 'Cajero',
                    dataSource: cajero,
                    labelProp: 'nombre_completo',
                    onClear: () => reset(),
                    breakPoints: {
                      md: 8,
                    },
                  }}
                  disbaled={!permission.view_usuario}
                />
              </Row>
            </Form>
            <Col span={24}>
              <Title level={4}>
                <Text>
                  Cortes encontrados
                </Text>
              </Title>
            </Col>
            <CustomTable
              cols={columns}
              data={data}
              rowSelection={rowSelection}
              mobileColIndex={0}
              customActions={getPrintOptions()}
              rowKey="id"
              handleOnRowClick={handleOnRowClick}
              allowEdit
              controls={{ onClickEdit }}
              permission={{
                change: permission.editar_corte,
                hasAllViewPermission: permission.hasAllViewPermission,
              }}
            />
          </Card>
        ) : (
          <Col span={24}>
            <Card
              bordered={false}
              className="form-container"
              title="Resumen del Corte"
              extra={(<FormSubmitCustomControls controls={controls} />)}
            >
              <Col span={24}>
                <Tabs type="card">
                  <TabPane
                    tab={(
                      <span>
                        <InfoCircleOutlined />
                        Detalles del corte
                      </span>
                    )}
                    key="detalles"
                  >
                    <Form
                      layout="vertical"
                      form={formUpdate}
                      onFinish={() => onFinish()}
                      scrollToFirstError
                    >
                      <Row gutter={10}>
                        <Col xs={24} sm={24} md={16} lg={8}>
                          <Form.Item
                            label="Terminal de Netpay"
                            name="terminal_de_netpay"
                            rules={entidad.solo_pago_netpay ? rules.required : null}
                          >
                            <Select dataSource={terminalesNetPay} />
                          </Form.Item>
                        </Col>
                        <Form.Item style={{ marginTop: 22 }}>
                          <Button htmlType="submit">
                            Guardar
                          </Button>
                        </Form.Item>
                      </Row>
                    </Form>
                  </TabPane>
                  <TabPane
                    tab={(
                      <span>
                        <EditOutlined />
                        Resumen de Recaudacion
                      </span>
                    )}
                    key="modificaciones"
                  >
                    <ResumenRegistroDeRecaudacion selectedRowKey={selectedRowKeys[0]} />
                  </TabPane>
                  <TabPane
                    tab={permission.retirocaja ? (
                      <span>
                        <CalculatorOutlined />
                        Retiros de Caja
                      </span>
                    ) : (
                      <Tooltip title=" No tiene permisos para hacer retiros">
                        <span>
                          <CalculatorOutlined />
                          Retiros de Caja
                        </span>
                      </Tooltip>
                    )}
                    key="retirosDeCaja"
                    disabled={!permission.retirocaja
                      || !recordData.el_corte_requiere_retiro_de_caja}
                  >
                    <Form
                      layout="vertical"
                      form={formRetiros}
                      onFinish={() => onFinishRetiro()}
                      scrollToFirstError
                      initialValues={{
                        estados_globales: 1,
                        moneda: entidad.moneda?.id,
                      }}
                    >
                      <Row gutter={10}>
                        <Col xs={24} sm={24} md={8}>
                          <NumericInput
                            decimal
                            name="importe"
                            label="Importe"
                            required
                          />
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="estados_globales"
                            label="Estado"
                            rules={rules.required}
                            hasFeedback
                          >
                            <Select disabled dataSource={estadosGlobales} />
                          </Form.Item>
                        </Col>
                        <Form.Item hidden>
                          <Button htmlType="submit" />
                        </Form.Item>
                      </Row>
                    </Form>
                  </TabPane>
                </Tabs>
              </Col>
            </Card>
          </Col>
        )}
      </Spin>
      <ModalReabrir
        visible={visibleModalReabrir}
        setVisible={setVisibleModalReabrir}
        callback={(values) => onFinishReabrir(values)}
      />
      <Previewer
        visible={visiblePreview}
        setVisible={(_visible) => {
          setVisiblePreview(_visible);
          setErrorPreviewer('');
        }}
        fileName={fileName}
        base64={base64}
        error={errorPreviewer}
      />
      <BusquedaAvanzadaUsers
        visible={visibleAdvancedSupervisor}
        setVisible={setVisibleAdvancedSupervisor}
        callback={(selected) => {
          const _selected = {
            ...selected,
            nombre_completo: `${selected?.first_name} ${selected?.last_name
              || ''} ${selected?.second_last_name || ''}`,
          };
          setCajero([_selected]);
          form.setFieldsValue({
            usuario_cajero: selected.id,
          });
        }}
        dataExterno={[]}
        setDataExterno={() => { }}
      />
    </Row>
  );
};

BuscarCorte.propTypes = {
  permission: PropTypes.shape({
    retirocaja: PropTypes.bool,
    view_usuario: PropTypes.bool,
    view_unidadderecaudacion: PropTypes.bool,
    editar_corte: PropTypes.bool,
    reabrir_corte: PropTypes.bool,
    hasAllViewPermission: PropTypes.bool,
  }).isRequired,
};

export default BuscarCorte;
